/** Gera várias receitas: uma por data programada e/ou uma por medicamento. */

import { todayYmdBr } from './localCalendarDate';

export type PrescriptionBatchJob = {
  issue_date: string;
  items: Record<string, unknown>[];
};

const ISO_YMD = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoYmdDate(value: unknown): boolean {
  return typeof value === 'string' && ISO_YMD.test(value);
}

export function normalizeScheduledIssueDates(dates: string[], fallbackToday = true): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of dates) {
    if (!isIsoYmdDate(raw)) continue;
    if (seen.has(raw)) continue;
    seen.add(raw);
    out.push(raw);
  }
  out.sort();
  if (!out.length && fallbackToday) out.push(todayYmdBr());
  return out;
}

export function isPrescriptionScheduledSpecialItem(
  item: { is_controlled?: boolean; is_antimicrobial?: boolean } | null | undefined,
): boolean {
  return Boolean(item?.is_controlled || item?.is_antimicrobial);
}

export function partitionPrescriptionItemsByScheduledSpecial(items: Record<string, unknown>[]) {
  const medicationItems = items.filter((i) => String(i.medication_name || '').trim());
  const regular: Record<string, unknown>[] = [];
  const special: Record<string, unknown>[] = [];
  medicationItems.forEach((item) => {
    if (isPrescriptionScheduledSpecialItem(item)) special.push(item);
    else regular.push(item);
  });
  return { regular, special, medicationItems };
}

function shouldScheduleSpecialItemsOnly(params: {
  format: 'structured' | 'free_text';
  scheduleSpecialItemsOnly?: boolean;
  scheduledDates: string[];
  regular: Record<string, unknown>[];
  special: Record<string, unknown>[];
}): boolean {
  return Boolean(
    params.scheduleSpecialItemsOnly
    && params.format === 'structured'
    && params.scheduledDates.length > 1
    && params.regular.length > 0
    && params.special.length > 0,
  );
}

export function countPrescriptionsToCreate(params: {
  format: 'structured' | 'free_text';
  medicationCount: number;
  splitMedications: boolean;
  scheduledDates: string[];
  items?: Record<string, unknown>[];
  scheduleSpecialItemsOnly?: boolean;
}): number {
  const dates = normalizeScheduledIssueDates(params.scheduledDates);
  const medCount = Math.max(0, params.medicationCount);
  if (params.format === 'free_text') return dates.length;
  if (medCount === 0) return 0;

  if (params.items?.length) {
    const { regular, special } = partitionPrescriptionItemsByScheduledSpecial(params.items);
    if (shouldScheduleSpecialItemsOnly({
      format: params.format,
      scheduleSpecialItemsOnly: params.scheduleSpecialItemsOnly,
      scheduledDates: dates,
      regular,
      special,
    })) {
      const regularJobs = regular.length > 0 ? 1 : 0;
      const specialJobs = countPrescriptionsToCreate({
        format: 'structured',
        medicationCount: special.length,
        splitMedications: params.splitMedications,
        scheduledDates: dates,
      });
      return regularJobs + specialJobs;
    }
  }

  if (params.splitMedications && medCount > 1) return dates.length * medCount;
  return dates.length;
}

export function buildPrescriptionBatchJobs(params: {
  format: 'structured' | 'free_text';
  items: Record<string, unknown>[];
  splitMedications: boolean;
  scheduledDates: string[];
  scheduleSpecialItemsOnly?: boolean;
}): PrescriptionBatchJob[] {
  const dates = normalizeScheduledIssueDates(params.scheduledDates);
  const medicationItems = params.items.filter((i) => String(i.medication_name || '').trim());

  if (params.format === 'free_text') {
    return dates.map((issue_date) => ({ issue_date, items: [] }));
  }

  if (!medicationItems.length) return [];

  const { regular, special } = partitionPrescriptionItemsByScheduledSpecial(medicationItems);

  if (shouldScheduleSpecialItemsOnly({
    format: params.format,
    scheduleSpecialItemsOnly: params.scheduleSpecialItemsOnly,
    scheduledDates: dates,
    regular,
    special,
  })) {
    const jobs: PrescriptionBatchJob[] = [];
    const firstDate = dates[0];
    jobs.push({
      issue_date: firstDate,
      items: regular.map((item, idx) => ({ ...item, sort_order: idx })),
    });
    const specialJobs = buildPrescriptionBatchJobs({
      format: 'structured',
      items: special,
      splitMedications: params.splitMedications,
      scheduledDates: dates,
      scheduleSpecialItemsOnly: false,
    });
    return [...jobs, ...specialJobs];
  }

  const split = params.splitMedications && medicationItems.length > 1;
  const jobs: PrescriptionBatchJob[] = [];

  for (const issue_date of dates) {
    if (split) {
      for (const item of medicationItems) {
        jobs.push({ issue_date, items: [{ ...item, sort_order: 0 }] });
      }
    } else {
      jobs.push({
        issue_date,
        items: medicationItems.map((item, idx) => ({ ...item, sort_order: idx })),
      });
    }
  }

  return jobs;
}

export function formatScheduledDatePtBr(isoDate: string): string {
  if (!isIsoYmdDate(isoDate)) return isoDate;
  const d = new Date(`${isoDate}T12:00:00`);
  return d.toLocaleDateString('pt-BR');
}
